import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { takeUntil, tap } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';
import { abbreviateAffix } from './name.util';

@Component({
  selector: 'formly-field-name',
  template: `
    <div class="mb-4">
      <h3 class="mb-2">
        {{ props.label }}
        <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
      </h3>
      <div class="dd-name-fields">
        @for (field of field.fieldGroup; track $index) {
          <formly-field [field]="field" [class]="field.className"></formly-field>
        }
      </div>
    </div>
    @if (field.props.description) {
      <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
    }
    <app-formly-error [field]="field"></app-formly-error>
  `,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    InfoTooltipComponent,
    FormlyErrorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldName extends FieldType implements OnInit {
  private destroy$ = injectDestroy();
  constructor() {
    super();
  }

  ngOnInit() {
    // generatedForm stores the name fieldGroup per module, so forms generated before shortName
    // existed lack the control. Add it here instead of requiring every module to be regenerated.
    const group = this.field.formControl as FormGroup;
    if (!group.get('shortName')) {
      group.addControl('shortName', new FormControl(''), { emitEvent: false });
    }

    this.field.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          // No "both parts filled" guard: createFullName() skips empty parts, so clearing a name
          // now clears fullName too instead of leaving a stale composite behind. The equality
          // check below stops the patch from re-triggering valueChanges endlessly.
          const fullName = this.createFullName();

          if (fullName != this.field.formControl.get('fullName').value) {
            this.field.formControl.get('fullName').patchValue(fullName, { emitEvent: true });
          }

          const shortName = this.createShortName();
          if (shortName != this.field.formControl.get('shortName').value) {
            this.field.formControl.get('shortName').patchValue(shortName, { emitEvent: true });
          }
        })
      )
      .subscribe();

    this.updateFullName();
  }

  /**
   * Composite display name from the filled parts only. An unfilled name must yield an empty
   * string — interpolating the raw controls wrote a literal "undefined undefined" into fullName
   * on init, which then surfaced verbatim wherever the composite is rendered (e.g. the wizard
   * review summary).
   */
  createFullName() {
    return [this.part('firstName'), this.part('affix'), this.part('lastName')].filter(Boolean).join(' ');
  }

  /** fullName with the affix cut to one letter per word, e.g. "Hesso vd Meulen" — for compact pickers. */
  createShortName() {
    return [this.part('firstName'), abbreviateAffix(this.part('affix')), this.part('lastName')].filter(Boolean).join(' ');
  }

  updateFullName() {
    this.field.formControl.get('fullName').patchValue(this.createFullName(), { emitEvent: true });
    this.field.formControl.get('shortName').patchValue(this.createShortName(), { emitEvent: true });
  }

  private part(key: string): string {
    const value = this.field.formControl.get(key)?.value;
    return typeof value === 'string' ? value.trim() : '';
  }
}
